"use strict";
/**
 * Workflow canvas graph — the structure the card canvas draws for a workflow
 * definition: which card each step is, the routes of a branch, the ports of a
 * card, the loop around it and the edges that return to a loop.
 *
 * Renderer-free: no positions, no shapes, no labels of step types. Positions
 * come from `layoutWorkflow`; the editor adds what it shows of a step type.
 * The server projects the same graph to place steps after changing a
 * workflow's structure.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.LOOP_EXIT_ROUTE = exports.LOOP_CONTINUE_ROUTE = void 0;
exports.projectWorkflowGraph = projectWorkflowGraph;
exports.withLoops = withLoops;
exports.outputIndexOf = outputIndexOf;
exports.insertPlaceOnEdge = insertPlaceOnEdge;
exports.draftGraphNode = draftGraphNode;
const control_flow_1 = require("./control-flow");
const workflow_edge_ports_1 = require("./workflow-edge-ports");
exports.LOOP_CONTINUE_ROUTE = 'continue';
exports.LOOP_EXIT_ROUTE = 'exit';
/** The canvas graph of a workflow definition. Throws for an edge to a step the definition lacks. */
function projectWorkflowGraph(definition) {
    const stepsById = new Map(definition.steps.map(step => [step.id, step]));
    for (const edge of definition.edges) {
        for (const stepId of [edge.sourceStepId, edge.targetStepId]) {
            if (!stepsById.has(stepId)) {
                throw new Error(`Workflow edge "${edge.id}" references unknown step "${stepId}"`);
            }
        }
    }
    const routeIds = resolveRouteIds(definition, stepsById);
    const routeLabels = (0, workflow_edge_ports_1.buildConditionLabels)(definition);
    return withLoops(definition.steps.map(step => toGraphNode(step, definition, routeLabels)), definition.edges.map(edge => ({
        id: edge.id,
        source: edge.sourceStepId,
        target: edge.targetStepId,
        routeId: routeIds.get(edge.id) ?? null,
    })));
}
/**
 * Completes a workflow that names the route of every edge: finds the body of
 * each loop, the loop every card lies in and the edges that return to a loop.
 */
function withLoops(nodes, edges) {
    const loopBodies = resolveLoopBodies(nodes, edges);
    return {
        nodes: nodes.map(node => ({
            ...node,
            loopId: innermostLoop(node.id, loopBodies),
        })),
        edges: edges.map(edge => ({
            ...edge,
            kind: isLoopBackEdge(edge, loopBodies) ? 'loop-back' : 'flow',
        })),
    };
}
/** The output of `source` an edge on `routeId` leaves from. */
function outputIndexOf(source, routeId) {
    if (source.kind === 'loop')
        return routeId === exports.LOOP_CONTINUE_ROUTE ? 0 : 1;
    if (source.kind !== 'branch')
        return 0;
    const at = source.routes.findIndex(route => route.id === routeId);
    return at < 0 ? 0 : at;
}
/**
 * The insert place on `edge`: a step put there leaves `source` where the edge
 * does and takes the place of the edge. The edge back to an empty loop is the
 * way into its body.
 */
function insertPlaceOnEdge(source, edge) {
    return {
        afterStepId: edge.source,
        outputIndex: edge.kind === 'loop-back' && edge.source === edge.target
            ? 0
            : outputIndexOf(source, edge.routeId),
        splitEdgeId: edge.id,
    };
}
/** The card of a step that is not part of a workflow yet: a step of the library. */
function draftGraphNode(id, stepType, title) {
    return {
        id,
        kind: cardKind(stepType),
        stepType,
        title,
        routes: [],
        inputs: [],
        outputs: [],
        loopId: null,
    };
}
function cardKind(stepType) {
    if (control_flow_1.CONTROL_BRANCH_TYPES.has(stepType))
        return 'branch';
    switch (stepType) {
        case 'start':
        case 'end':
        case 'merge':
        case 'loop':
            return stepType;
        default:
            return 'step';
    }
}
function toGraphNode(step, definition, routeLabels) {
    const kind = cardKind(step.type);
    return {
        id: step.id,
        kind,
        stepType: step.type,
        title: step.name,
        routes: kind === 'branch' ? branchRoutes(step, routeLabels) : [],
        inputs: inputPorts(step, definition),
        outputs: outputPorts(step, definition),
    };
}
function branchRoutes(step, routeLabels) {
    const ports = step.config['ports'];
    return (ports ?? []).map(port => ({
        id: port.portId,
        label: routeLabels.get(`${step.id}:${port.portId}`) ?? port.portId,
    }));
}
function inputPorts(step, definition) {
    if (step.type === 'start')
        return [];
    if (step.type === 'end')
        return schemaPorts(definition.outputSchema.fields);
    return slotPorts(step.inputSlots);
}
function outputPorts(step, definition) {
    if (step.type === 'end')
        return [];
    if (step.type === 'start')
        return schemaPorts(definition.inputSchema.fields);
    return slotPorts((0, control_flow_1.deriveControlNodeOutputSlots)(`swr.${step.type}`) ?? step.outputSlots);
}
function slotPorts(slots) {
    return (slots ?? []).map(slot => ({
        id: slot.id,
        label: slot.name,
        type: slot.type,
    }));
}
function schemaPorts(fields) {
    return fields.map(field => ({
        id: field.id,
        label: field.name,
        type: field.type,
    }));
}
/**
 * Port an edge leaves from. Every edge leaving a loop is `continue` unless it
 * resolves to `exit` — the same rule the stored output index 0 expresses.
 */
function resolveRouteIds(definition, stepsById) {
    const branchAssignments = (0, workflow_edge_ports_1.buildBranchEdgeAssignments)(definition);
    const loopAssignments = (0, workflow_edge_ports_1.buildLoopEdgeAssignments)(definition);
    const routeIds = new Map();
    for (const edge of definition.edges) {
        const routeId = stepsById.get(edge.sourceStepId)?.type === 'loop'
            ? (loopAssignments.get(edge.id)?.sourcePort ?? exports.LOOP_CONTINUE_ROUTE)
            : (branchAssignments.get(edge.id)?.sourcePort ?? edge.sourcePort);
        if (routeId != null)
            routeIds.set(edge.id, routeId);
    }
    return routeIds;
}
/** Body of each loop: every step reachable from its `continue` edges before the flow returns to the loop. */
function resolveLoopBodies(nodes, edges) {
    const edgesBySource = new Map();
    for (const edge of edges) {
        const leaving = edgesBySource.get(edge.source) ?? [];
        leaving.push(edge);
        edgesBySource.set(edge.source, leaving);
    }
    const bodies = new Map();
    for (const node of nodes) {
        if (node.kind !== 'loop')
            continue;
        const body = new Set();
        const queue = (edgesBySource.get(node.id) ?? [])
            .filter(edge => edge.routeId === exports.LOOP_CONTINUE_ROUTE)
            .map(edge => edge.target);
        for (let next = queue.shift(); next != null; next = queue.shift()) {
            if (next === node.id || body.has(next))
                continue;
            body.add(next);
            for (const edge of edgesBySource.get(next) ?? []) {
                queue.push(edge.target);
            }
        }
        bodies.set(node.id, body);
    }
    return bodies;
}
function innermostLoop(stepId, loopBodies) {
    let innermost = null;
    for (const [loopId, body] of loopBodies) {
        if (!body.has(stepId))
            continue;
        if (!innermost || body.size < innermost.size) {
            innermost = { loopId, size: body.size };
        }
    }
    return innermost?.loopId ?? null;
}
function isLoopBackEdge(edge, loopBodies) {
    const body = loopBodies.get(edge.target);
    if (!body)
        return false;
    return edge.source === edge.target || body.has(edge.source);
}
